import { useState } from "react";
import { useGovernanceDecisions, useProjects } from "../../api/queries";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { GovernanceVerdictPill } from "../../components/StatePill";
import { AdminGate } from "../admin/AdminGate";
import { toGovernanceDecision, type GovernanceDecision } from "./governanceDecision";
import { GovernanceFindingsPanel } from "./GovernanceFindingsPanel";
import "./governance.css";

const PAGE_LIMIT = 50;

/** Shortens a git commit sha for the table (mirrors CorpusPage's shortCommit — the
 * governance commit IS a corpus commit, ADR-0052/0058). Full value stays in `title` for
 * copy/paste. */
function shortCommit(sha: string): string {
  return sha.length > 12 ? sha.slice(0, 12) : sha;
}

function actorLabel(decision: GovernanceDecision): string {
  if (!decision.actorUserId) return "system";
  return decision.actorRole ? `${decision.actorUserId} (${decision.actorRole})` : decision.actorUserId;
}

const COLUMNS: DataTableColumn<GovernanceDecision>[] = [
  {
    key: "time",
    header: "Time",
    cell: (d) => new Date(d.occurredAt).toLocaleString(),
    className: "data-table__secondary",
  },
  { key: "verdict", header: "Verdict", cell: (d) => <GovernanceVerdictPill verdict={d.verdict} /> },
  {
    key: "promotion",
    header: "Promotion",
    cell: (d) =>
      d.parsed ? (
        <span className="governance-promotion">
          <span className="data-table__mono">{d.parsed.sourceEnvId}</span>
          <span className="governance-promotion__arrow" aria-hidden="true">
            →
          </span>
          <span className="data-table__mono">{d.parsed.targetEnvId}</span>
        </span>
      ) : (
        "—"
      ),
  },
  { key: "service", header: "Service", cell: (d) => d.serviceId || "—", className: "data-table__mono" },
  {
    key: "rules",
    header: "Fired rule(s)",
    cell: (d) => {
      if (!d.parsed) return "—";
      if (d.parsed.firedRules.length === 0) return d.verdict === "allow" ? "None" : "—";
      return d.parsed.firedRules.join(", ");
    },
    className: "data-table__mono",
  },
  {
    key: "image",
    header: "Image",
    cell: (d) => (d.parsed ? d.parsed.image : "—"),
    className: "data-table__mono governance-decisions__image",
  },
  {
    key: "commit",
    header: "Governance commit",
    cell: (d) =>
      d.parsed ? (
        <span className="data-table__mono" title={d.parsed.governanceCommit}>
          {shortCommit(d.parsed.governanceCommit)}
        </span>
      ) : (
        "—"
      ),
  },
  { key: "actor", header: "Principal", cell: (d) => actorLabel(d) },
];

function GovernanceDecisionsList() {
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useGovernanceDecisions({
    cursor: paginator.cursor,
    limit: PAGE_LIMIT,
  });

  const decisions = data?.items.map(toGovernanceDecision);

  return (
    <>
      <PageHeader
        title="Governance"
        description={
          "Every governed-promotion decision your declared governance produced — allow and deny — " +
          "tied to the exact governance commit it was evaluated against (ADR-0058). This is the " +
          "\"did my policy actually bite\" surface: a denied promotion below means the gate refused it."
        }
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading governance decisions…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load governance decisions. Check your connection and try again."
        }
        isEmpty={Boolean(decisions && decisions.length === 0)}
        empty={
          <EmptyState
            title="No governance decisions yet"
            description={
              "Nothing has been evaluated against declared governance yet. Once a project with a " +
              "promotion policy (a governance_rule with a promote.* predicate) attempts a promotion, " +
              "the allow/deny decision shows up here."
            }
          />
        }
      >
        {decisions && (
          <>
            <DataTable columns={COLUMNS} rows={decisions} rowKey={(d) => d.id} />
            <PaginatorControls
              itemCount={decisions.length}
              nextCursor={data?.nextCursor}
              paginator={paginator}
              noun="decisions"
            />
          </>
        )}
      </QueryStates>

      <GovernanceFindingsSection />
    </>
  );
}

/**
 * The governing-agent findings surface (ADR-0059), scoped to a chosen project (the findings API is
 * per-project). It sits below the tenant-wide decisions table: decisions are what the deterministic
 * engine DID; findings are the objections a producer raised that a human must resolve. Defaults to
 * the first project; a picker switches scope.
 */
function GovernanceFindingsSection() {
  const { data: projects } = useProjects();
  const items = projects?.items ?? [];
  const [selected, setSelected] = useState<string>("");
  const projectId = selected || items[0]?.id || "";

  if (items.length === 0) {
    return null;
  }
  return (
    <div className="governance-findings-section">
      <label className="governance-findings-section__picker">
        Project
        <select value={projectId} onChange={(e) => setSelected(e.target.value)}>
          {items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {projectId && <GovernanceFindingsPanel projectId={projectId} />}
    </div>
  );
}

/**
 * The Platform Lead's "observe" surface for ADR-0058's governed-promotion gate
 * (#562): read-only, newest-first, GOVERNANCE-shaped rendering of `governance.decision`
 * audit events (never raw audit rows — see `toGovernanceDecision`). Reuses the same
 * `GET /api/audit-events` read path as the Audit log (`useAuditEvents`/`AuditPage`),
 * narrowed server-side to `action=governance.decision` (A-5: one audit fetch mechanism,
 * not a second one). Admin-gated like the Audit log itself, since it reads the same
 * `audit_log:view`-scoped endpoint (AdminGate is UX only — the server enforces the real
 * decision, S-8).
 */
export function GovernanceDecisionsPage() {
  return (
    <AdminGate>
      <GovernanceDecisionsList />
    </AdminGate>
  );
}
